import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const FOUNDER_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/files/zalta/uploads/products/7079acdc-53f8-452f-bb9d-9a8f5e72a551.jpeg`;

const ease = [0.16, 1, 0.3, 1];
const ranges = ["Housekeeping Chemicals", "Guest Amenities", "Housekeeping Tools", "Fragrance Solutions"];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Journey = () => (
  <section id="story" data-testid="journey-section" className="border-y border-[#221533]/10 bg-white">
    <div className="max-w-[1500px] mx-auto px-5 sm:px-6 lg:px-12 py-20 sm:py-28 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
      <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
        <Reveal>
          <p className="font-mono2 text-[11px] tracking-[0.35em] uppercase text-[#8E5A9E] mb-6">Our Brand Story</p>
          <h2 className="font-clash text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] text-[#221533]">
            Better Quality.<br />
            <span className="italic text-[#8E5A9E]">Smarter Pricing.</span><br />
            Complete Institutional Solutions.
          </h2>
          <p className="mt-8 font-mono2 text-[11px] tracking-[0.25em] uppercase text-[#221533]/50 leading-loose">
            10+ Years of Experience in<br />Institutional Hygiene &amp; Guest Care
          </p>
          <div data-testid="story-founder-card" className="mt-10 max-w-sm">
            <div className="relative rounded-2xl overflow-hidden border border-[#221533]/10 shadow-[0_30px_70px_-25px_rgba(142,90,158,0.45)]">
              <img
                src={FOUNDER_IMG}
                alt="Paramjeet Singh Ahluwalia — Founder, ZALTA"
                data-testid="story-founder-photo"
                className="w-full aspect-[4/5] object-cover object-top"
              />
              <div className="absolute bottom-0 inset-x-0 bg-white/90 backdrop-blur-md border-t border-[#221533]/10 px-5 py-4">
                <p className="font-clash text-xl font-semibold text-[#221533] leading-tight">Paramjeet Singh Ahluwalia</p>
                <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#8E5A9E] mt-1">Founder — ZALTA®</p>
                <span className="inline-block mt-2 bg-[#8E5A9E] text-white font-manrope font-semibold text-xs px-3 py-1 rounded-full">
                  MBA (Finance &amp; Marketing)
                </span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-7 space-y-14">
        <Reveal className="space-y-5 text-[#221533]/65 font-light text-base lg:text-lg leading-relaxed">
          <p>
            ZALTA is an Indian institutional brand with more than 10 years of experience in the
            manufacturing and supply of Housekeeping Chemicals, Guest Amenities, Housekeeping Tools
            and Fragrance Solutions for hotels, hospitals, medical colleges, schools and other institutions.
          </p>
          <p>
            ZALTA was created with a simple but powerful objective —
            <span className="text-[#221533] font-normal"> to help institutions reduce their operating
            costs without compromising on quality.</span>
          </p>
          <p>
            For years, premium brands in the institutional hygiene segment offered good-quality products,
            but often at significantly higher prices. We believed professional quality should not come
            with an unnecessarily high price tag. <span className="text-[#221533] font-normal">That belief
            became the foundation of ZALTA.</span>
          </p>
        </Reveal>

        <Reveal className="border-t border-[#221533]/10 pt-10">
          <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#8E5A9E] mb-5">Built Through Research &amp; Development</p>
          <div className="space-y-5 text-[#221533]/65 font-light text-base lg:text-lg leading-relaxed">
            <p>
              Before launching ZALTA, we invested significant time, resources and R&amp;D into developing
              products that deliver performance comparable to expensive brands while remaining affordable
              and practical for institutional budgets.
            </p>
            <p>
              Our objective was not simply to create low-cost products — it was to create
              <span className="text-[#221533] font-normal"> better-value products</span>: the quality,
              performance and reliability institutions expect, at a price that helps them manage budgets efficiently.
            </p>
          </div>
        </Reveal>

        <Reveal className="border-t border-[#221533]/10 pt-10">
          <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#8E5A9E] mb-5">India's Affordable Complete Institutional Solution</p>
          <p className="text-[#221533]/65 font-light text-base lg:text-lg leading-relaxed mb-6">
            ZALTA was developed as an Indian affordable institutional brand offering a complete range
            of solutions under one brand — a convenient and reliable source for everyday housekeeping
            and guest-care requirements.
          </p>
          <div className="flex flex-wrap gap-2">
            {ranges.map((r) => (
              <span key={r} className="font-mono2 text-[11px] tracking-[0.15em] uppercase border border-[#8E5A9E]/30 text-[#74427F] bg-[#8E5A9E]/[0.06] px-4 py-2 rounded-full">
                {r}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal className="border-t border-[#221533]/10 pt-10">
          <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#8E5A9E] mb-5">Direct From Factory to Client</p>
          <p className="text-[#221533]/65 font-light text-base lg:text-lg leading-relaxed mb-8">
            By reducing unnecessary layers between manufacturing and the end customer, we maintain
            better control over quality while keeping products more affordable and accessible.
          </p>
          <div className="flex items-center flex-wrap gap-3 font-clash text-xl sm:text-2xl font-medium text-[#221533]">
            <span className="border border-[#221533]/15 px-5 py-3 rounded-md bg-[#FAF8FC]">Factory</span>
            <ArrowRight size={20} className="text-[#8E5A9E]" />
            <span className="border border-[#8E5A9E] px-5 py-3 rounded-md bg-[#8E5A9E] text-white">ZALTA<sup className="text-[10px]">®</sup></span>
            <ArrowRight size={20} className="text-[#8E5A9E]" />
            <span className="border border-[#221533]/15 px-5 py-3 rounded-md bg-[#FAF8FC]">Client</span>
          </div>
        </Reveal>

        <Reveal className="border-t border-[#221533]/10 pt-10">
          <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#8E5A9E] mb-5">Our Promise</p>
          <p className="font-clash text-3xl sm:text-4xl font-semibold leading-tight text-[#221533]">
            Premium-Grade Performance.<br />
            <span className="italic text-[#8E5A9E]">Affordable Institutional Pricing.</span>
          </p>
          <p className="mt-6 text-[#221533]/65 font-light text-base lg:text-lg leading-relaxed">
            We continuously work to give our clients products that help them achieve better quality,
            lower operating costs and greater value from every purchase.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);
